import React, { useEffect, useMemo, useState } from "react";
import api, { dkk, num, dateDK, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Pencil, Search, Layers, Tag, Package, ArrowLeftRight, Calendar, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CAR_BRANDS, CAR_MODELS } from "../lib/carData";
import DeleteCarDialog from "../components/DeleteCarDialog";

// Faste omkostninger pr. bil. To grupper:
//  - "no_vat": uden moms-fradrag (forsikring, vejhjælp, vægtafgift, lån)
//  - "vat":    med moms-fradrag  (nøglefri, leasing)
const FIXED_COST_FIELDS = [
  { key: "forsikring",  label: "Forsikring",  suffix: "/ md",     monthly_factor: 1,    group: "no_vat" },
  { key: "vejhjaelp",   label: "Vejhjælp",    suffix: "/ md",     monthly_factor: 1,    group: "no_vat" },
  { key: "vaegtafgift", label: "Vægtafgift",  suffix: "/ 6 mdr",  monthly_factor: 1/6,  group: "no_vat" },
  { key: "laan",        label: "Lån",         suffix: "/ md",     monthly_factor: 1,    group: "no_vat" },
  { key: "noeglefri",   label: "Nøglefri",    suffix: "/ md",     monthly_factor: 1,    group: "vat" },
  { key: "leasing",     label: "Leasing",     suffix: "/ md",     monthly_factor: 1,    group: "vat" },
];

const EXTRA_FREQ = {
  monthly:    { label: "Månedligt",    factor: 1 },
  halfyearly: { label: "Hvert halve år", factor: 1/6 },
  yearly:     { label: "Årligt",       factor: 1/12 },
  once:       { label: "Engangs",      factor: 0 },
};

function calcMonthlyCost(car) {
  let total = 0;
  FIXED_COST_FIELDS.forEach((f) => {
    total += Number(car?.fixed_costs?.[f.key] || 0) * f.monthly_factor;
  });
  (car?.extra_costs || []).forEach((ec) => {
    const meta = EXTRA_FREQ[ec.frequency] || EXTRA_FREQ.monthly;
    total += Number(ec.amount || 0) * meta.factor;
  });
  return total;
}

function hasServiceData(car) {
  const km = car?.last_service_km;
  const cur = car?.current_km;
  const kmOk = km !== null && km !== undefined && km !== "" && Number.isFinite(Number(km));
  const curOk = cur !== null && cur !== undefined && cur !== "" && Number.isFinite(Number(cur));
  return kmOk && curOk;
}

function kmWarn(car) {
  const km = Number(car?.current_km || 0);
  return km >= 350000 ? "red" : null;
}

function yearWarn(car) {
  const y = Number(car?.year || 0);
  if (!y) return null;
  const age = new Date().getFullYear() - y;
  if (age >= 18) return "red";
  if (age >= 15) return "amber";
  return null;
}

function SortBtn({ k, cur, dir, onClick, children }) {
  const active = cur === k;
  const arrow = active ? (dir === "asc" ? "↑" : "↓") : "";
  return (
    <button
      type="button"
      onClick={() => onClick(k)}
      className={"inline-flex items-center gap-1 hover:text-white transition-colors " + (active ? "text-[#C8A661]" : "")}
    >
      {children}
      {arrow && <span className="text-[10px]">{arrow}</span>}
    </button>
  );
}

export default function Cars({ status = "active", platform = null }) {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [openCreate, setOpenCreate] = useState(false);
  const [editCar, setEditCar] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [scheduleCar, setScheduleCar] = useState(null);
  const [deleteCar, setDeleteCar] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [carbuddiEarningCar, setCarbuddiEarningCar] = useState(null);

  const isLager = status === "lager";
  const isCarbuddi = platform === "carbuddi";

  const load = () => {
    setLoading(true);
    setSelected({});
    api
      .get("/cars", { params: { status_filter: status, include_revenue: status === "lager" || status === "active" } })
      .then((r) => {
        const data = platform ? r.data.filter((c) => (c.platform || "gomore") === platform) : r.data;
        setCars(data);
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, platform]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    let arr = !s ? [...cars] : cars.filter(
      (c) =>
        c.license_plate?.toLowerCase().includes(s) ||
        c.make?.toLowerCase().includes(s) ||
        c.model?.toLowerCase().includes(s),
    );
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      arr.sort((a, b) => {
        const get = (c) => {
          switch (sortKey) {
            case "plate": return c.license_plate || "";
            case "year": return Number(c.year || 0);
            case "km": return Number(c.current_km || 0);
            case "purchase": return Number(c.purchase_price || 0);
            default: return 0;
          }
        };
        const av = get(a), bv = get(b);
        if (typeof av === "string") return av.localeCompare(bv) * dir;
        return (av - bv) * dir;
      });
    }
    return arr;
  }, [cars, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  // Status-change modal state
  const [moveCar, setMoveCar] = useState(null);  // { car, newStatus }

  const requestMove = (car, newStatus) => {
    if (newStatus === "sold") {
      // Solgt: åbn redigér-dialogen direkte (så bruger kan udfylde pris + dato)
      setEditCar({ ...car, status: "sold" });
      return;
    }
    setMoveCar({ car, newStatus });
  };

  const performMove = async ({ car, newStatus, updateCosts, effectiveDate, newCosts }) => {
    const body = { status: newStatus };
    if (updateCosts && newCosts) {
      body.fixed_costs = newCosts;
    }
    try {
      await api.patch(`/cars/${car.id}`, body);
      const label = { active: "Aktiv", lager: "Lager" }[newStatus] || newStatus;
      toast.success(
        `${car.license_plate} flyttet til ${label}` +
        (updateCosts ? ` · omkostninger opdateret (gælder fra ${effectiveDate})` : "")
      );
      setMoveCar(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl" data-testid="cars-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-10">
        <div>
          <div className="overline text-zinc-400">{isCarbuddi ? "Carbuddi" : (isLager ? "Lager" : "Biler")}</div>
          <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2">{isCarbuddi ? "Carbuddi biler" : (isLager ? "På lager" : "Aktive biler")}</h1>
          <p className="text-zinc-500 text-xs sm:text-sm mt-1">
            {isCarbuddi
              ? "Biler i aktiv drift via Carbuddi. Dobbelt-klik en bil for at registrere månedsindtjening (inkl. moms)."
              : (isLager
                ? "Biler du har købt men endnu ikke kører i drift. Klar til at tage i drift eller sælge."
                : "Biler i aktiv drift. Beløb er ex. moms.")}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            disabled={selectedIds.length === 0}
            onClick={() => setBulkOpen(true)}
            className="rounded-none border-zinc-700 flex-1 sm:flex-none"
            data-testid="bulk-edit-button"
          >
            <Layers className="w-4 h-4 mr-2" /> Rediger {selectedIds.length || ""} valgte
          </Button>
          {selectedIds.length > 0 && (
            <Button
              variant="outline"
              onClick={() => {
                const carsToDelete = cars.filter((c) => selectedIds.includes(c.id));
                setDeleteCar(carsToDelete);
              }}
              className="rounded-none border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300 flex-1 sm:flex-none"
              data-testid="bulk-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Slet {selectedIds.length} valgte
            </Button>
          )}
          <Button
            onClick={() => setOpenCreate(true)}
            className="rounded-none bg-white text-black hover:bg-zinc-200 flex-1 sm:flex-none"
            data-testid="add-car-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Tilføj bil
          </Button>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          placeholder="Søg på nummerplade, mærke eller model"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-none border-zinc-700"
          data-testid="cars-search-input"
        />
      </div>

      <div className="section-card overflow-x-auto">
        <table className="w-full dense min-w-[900px]" data-testid="cars-table">
          <thead className="table-head">
            <tr>
              <th className="w-10">
                <Checkbox
                  checked={selectedIds.length > 0 && selectedIds.length === filtered.length}
                  onCheckedChange={(v) => {
                    if (v) {
                      const next = {};
                      filtered.forEach((c) => (next[c.id] = true));
                      setSelected(next);
                    } else setSelected({});
                  }}
                  data-testid="select-all-checkbox"
                />
              </th>
              <th>
                <SortBtn k="plate" cur={sortKey} dir={sortDir} onClick={toggleSort}>Nummerplade</SortBtn>
              </th>
              {isLager ? (
                <th className="text-zinc-500 font-normal">Bil</th>
              ) : (
                <th className="text-zinc-500 font-normal text-xs">Bil</th>
              )}
              <th className={isLager ? "" : "text-zinc-500 font-normal text-xs"}>
                <SortBtn k="year" cur={sortKey} dir={sortDir} onClick={toggleSort}>Årgang</SortBtn>
              </th>
              <th className={isLager ? "" : "text-zinc-500 font-normal text-xs"}>
                <SortBtn k="km" cur={sortKey} dir={sortDir} onClick={toggleSort}>Km nu</SortBtn>
              </th>
              {isLager ? (
                <>
                  <th className="text-right">Lagerdage</th>
                  <th className="text-right">Køb + 3.500</th>
                  <th className="text-right">Udlejning</th>
                  <th className="text-right">Står dig i</th>
                  <th className="text-right">Min. salg <span className="text-zinc-600 font-normal normal-case">(break-even)</span></th>
                </>
              ) : (
                <>
                  <th className="text-right">
                    <SortBtn k="purchase" cur={sortKey} dir={sortDir} onClick={toggleSort}>Køb</SortBtn>
                  </th>
                  <th className="text-right">Netto indtjening <span className="text-zinc-600 font-normal normal-case">(ex moms − omk.)</span></th>
                  <th className="text-right">Står dig i</th>
                  <th className="text-right">Tjent hjem</th>
                </>
              )}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={isLager ? 10 : 8} className="p-8 text-center text-zinc-400">
                  Indlæser…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={isLager ? 10 : 8} className="p-8 text-center text-zinc-400">
                  {isLager
                    ? "Ingen biler på lager. Tryk \"Tilføj bil\" og vælg status \"Lager\"."
                    : "Ingen biler endnu. Tilføj én eller importér din CSV."}
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const missing = !isLager && !hasServiceData(c);
              return (
                <tr
                  key={c.id}
                  className={"table-row " + (missing ? "row-warning" : "") + (isCarbuddi ? " cursor-pointer" : "")}
                  data-testid={`car-row-${c.license_plate}`}
                  onDoubleClick={isCarbuddi ? () => setCarbuddiEarningCar(c) : undefined}
                  title={isCarbuddi ? "Dobbelt-klik for at registrere månedsindtjening" : undefined}
                >
                  <td>
                    <Checkbox
                      checked={!!selected[c.id]}
                      onCheckedChange={(v) =>
                        setSelected((s) => ({ ...s, [c.id]: !!v }))
                      }
                      data-testid={`select-car-${c.license_plate}`}
                    />
                  </td>
                  <td className="font-display font-bold tracking-tight">
                    {c.license_plate}
                    {!isLager && (() => {
                      const days = c.days_since_inspection;
                      if (days == null) {
                        return (
                          <div className="text-[9px] uppercase tracking-wider text-red-400 font-normal mt-0.5">
                            Aldrig kontrolleret
                          </div>
                        );
                      }
                      const overdue = days > 30;
                      const warn = days > 20 && !overdue;
                      const cls = overdue ? "text-red-400" : warn ? "text-amber-400" : "text-zinc-500";
                      return (
                        <div className={"text-[9px] uppercase tracking-wider font-normal mt-0.5 " + cls}>
                          {days} dage siden kontrol{overdue && " · overskredet"}
                        </div>
                      );
                    })()}
                  </td>
                  <td className={isLager ? "" : "text-zinc-500 text-xs"}>
                    {c.make || c.model ? (
                      <div>
                        <div className={isLager ? "font-medium" : ""}>{c.make}</div>
                        <div className="text-xs text-zinc-500">{c.model}</div>
                      </div>
                    ) : (
                      <span className="text-red-600 text-xs font-semibold">Mangler data</span>
                    )}
                  </td>
                  <td className={isLager ? "" : "text-xs " + (yearWarn(c) === "red" ? "text-red-400 font-semibold" : yearWarn(c) === "amber" ? "text-amber-400" : "text-zinc-500")}>
                    {c.year || "—"}
                  </td>
                  <td className={isLager ? "" : "text-xs tabular-nums " + (kmWarn(c) === "red" ? "text-red-400 font-semibold" : "text-zinc-500")}>
                    {c.current_km != null ? num(c.current_km) : <span className="text-red-600 text-xs">Mangler</span>}
                  </td>
                  {isLager ? (
                    (() => {
                      const PREP = 3500;
                      const purchase = Number(c.purchase_price || 0);
                      const extras = (c.extra_costs || []).reduce((s, ec) => {
                        const amt = Number(ec.amount || 0);
                        return s + (ec.vat_included ? amt / 1.25 : amt);
                      }, 0);
                      const rentalEx = Number(c.total_rental_revenue || 0) / 1.25;
                      const grossCost = purchase + PREP + extras;
                      const netCost = Math.max(0, grossCost - rentalEx);
                      const minSale = purchase > 0
                        ? Math.max(grossCost, (netCost - 0.2 * purchase) / 0.8)
                        : grossCost;
                      // Lagerdage
                      let lagerDays = null;
                      const ls = c.lager_since || (c.created_at ? c.created_at.slice(0, 10) : null);
                      if (ls) {
                        try {
                          const lsDate = new Date(ls + "T00:00:00Z");
                          const now = new Date();
                          lagerDays = Math.max(0, Math.floor((now - lsDate) / 86400000));
                        } catch (_) {}
                      }
                      const lagerColor = lagerDays == null ? "text-zinc-700"
                        : lagerDays > 90 ? "text-red-400"
                        : lagerDays > 30 ? "text-amber-400"
                        : "text-zinc-300";
                      return (
                        <>
                          <td className="text-right tabular-nums">
                            <div className={lagerColor + " font-semibold"}>{lagerDays != null ? `${lagerDays} dage` : "—"}</div>
                            {ls && <div className="text-[10px] text-zinc-600">siden {dateDK(ls)}</div>}
                          </td>
                          <td className="text-right tabular-nums">
                            <div className="text-white">{purchase ? dkk(grossCost) : "—"}</div>
                            <div className="text-[10px] text-zinc-500">
                              {purchase
                                ? `${dkk(purchase)} + 3.500${extras > 0 ? ` + ekstra ${dkk(extras)}` : ""}`
                                : "Mangler købspris"}
                            </div>
                          </td>
                          <td className="text-right tabular-nums text-[#C8A661]">
                            {rentalEx > 0 ? dkk(rentalEx) : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="text-right tabular-nums font-semibold">
                            {purchase ? dkk(netCost) : "—"}
                          </td>
                          <td className="text-right tabular-nums">
                            <div className="text-emerald-400 font-semibold">
                              {purchase ? dkk(minSale) : "—"}
                            </div>
                            {(rentalEx > 0 || extras > 0) && purchase > 0 && (
                              <div className="text-[10px] text-zinc-500">inkl. brugtmoms</div>
                            )}
                          </td>
                        </>
                      );
                    })()
                  ) : (
                    (() => {
                      const PREP = 3500;
                      const purchase = Number(c.purchase_price || 0);
                      const extras = (c.extra_costs || []).reduce((s, ec) => {
                        const amt = Number(ec.amount || 0);
                        return s + (ec.vat_included ? amt / 1.25 : amt);
                      }, 0);
                      const realCost = purchase ? purchase + PREP + extras : 0;
                      const monthlyFixedEx = calcMonthlyCost(c); // already ex moms
                      const months = c.active_months_count || 0;
                      const rentalEx = Number(c.total_rental_revenue || 0) / 1.25;
                      const totalFixedAccrued = monthlyFixedEx * months;
                      const netRental = rentalEx - totalFixedAccrued;
                      const balance = realCost - netRental; // Det bilen står dig i lige nu
                      const paidBack = balance <= 0;
                      // Forecast: gennemsnitlig netto-indtjening pr. måned (kun positive måneder regnes)
                      const avgNetPerMonth = months > 0 ? netRental / months : 0;
                      let forecastText = "—";
                      let forecastTip = "";
                      if (paidBack) {
                        forecastText = "Betalt";
                        forecastTip = "Bilen har tjent sig selv hjem";
                      } else if (months === 0 || avgNetPerMonth <= 0) {
                        forecastText = "—";
                        forecastTip = months === 0 ? "Ingen kørsel registreret endnu" : "Negativt månedsgns. — kan ikke estimeres";
                      } else {
                        const monthsRemaining = balance / avgNetPerMonth;
                        const target = new Date();
                        target.setMonth(target.getMonth() + Math.ceil(monthsRemaining));
                        const NAMES = ["Jan","Feb","Mar","Apr","Maj","Jun","Jul","Aug","Sep","Okt","Nov","Dec"];
                        forecastText = `${NAMES[target.getMonth()]} ${target.getFullYear()}`;
                        forecastTip = `Ca. ${Math.ceil(monthsRemaining)} mdr. fra nu — baseret på gns. ${dkk(avgNetPerMonth)}/md`;
                      }
                      return (
                        <>
                          <td className="text-right tabular-nums text-zinc-300">
                            {purchase ? dkk(purchase) : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="text-right tabular-nums">
                            <div className={netRental >= 0 ? "text-emerald-400" : "text-red-400"}>
                              {months > 0 ? dkk(netRental) : <span className="text-zinc-700">—</span>}
                            </div>
                            {months > 0 && (
                              <div className="text-[10px] text-zinc-500">
                                {dkk(rentalEx)} − {dkk(totalFixedAccrued)} ({months} mdr.)
                              </div>
                            )}
                          </td>
                          <td className="text-right tabular-nums font-semibold">
                            {purchase ? (
                              <span className={paidBack ? "text-emerald-400" : "text-white"}>
                                {paidBack ? `−${dkk(Math.abs(balance))}` : dkk(balance)}
                              </span>
                            ) : (
                              <span className="text-zinc-700">—</span>
                            )}
                            {paidBack && <div className="text-[10px] text-emerald-500">overskud</div>}
                          </td>
                          <td className="text-right tabular-nums" title={forecastTip}>
                            <div className={paidBack ? "text-emerald-400 font-semibold" : "text-[#C8A661]"}>
                              {forecastText}
                            </div>
                          </td>
                        </>
                      );
                    })()
                  )}
                  <td className="text-right whitespace-nowrap">
                    <button
                      className="text-zinc-500 hover:text-white mr-3"
                      onClick={() => setEditCar(c)}
                      data-testid={`edit-car-${c.license_plate}`}
                      title="Rediger"
                    >
                      <Pencil className="w-4 h-4 inline" />
                    </button>
                    {isLager ? (
                      <button
                        className="text-zinc-500 hover:text-emerald-700"
                        onClick={() => requestMove(c, "active")}
                        data-testid={`activate-car-${c.license_plate}`}
                        title="Sæt i drift"
                      >
                        <ArrowLeftRight className="w-4 h-4 inline" />
                      </button>
                    ) : (
                      <>
                        <button
                          className={"mr-3 " + (c.scheduled_lager_date ? "text-[#C8A661]" : "text-zinc-500 hover:text-white")}
                          onClick={() => setScheduleCar(c)}
                          data-testid={`schedule-lager-${c.license_plate}`}
                          title={c.scheduled_lager_date ? `Rykkes til lager: ${dateDK(c.scheduled_lager_date)}` : "Plan udfasning"}
                        >
                          <Calendar className="w-4 h-4 inline" />
                        </button>
                        <button
                          className="text-zinc-500 hover:text-white mr-3"
                          onClick={() => requestMove(c, "lager")}
                          data-testid={`lager-car-${c.license_plate}`}
                          title="Sæt på lager nu"
                        >
                          <Package className="w-4 h-4 inline" />
                        </button>
                        <button
                          className="text-zinc-500 hover:text-white"
                          onClick={() => requestMove(c, "sold")}
                          data-testid={`sell-car-${c.license_plate}`}
                          title="Marker som solgt"
                        >
                          <Tag className="w-4 h-4 inline" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CarDialog
        open={openCreate || !!editCar}
        onOpenChange={(v) => {
          if (!v) {
            setOpenCreate(false);
            setEditCar(null);
          }
        }}
        car={editCar}
        defaultStatus={status}
        onSaved={() => {
          setOpenCreate(false);
          setEditCar(null);
          load();
        }}
      />

      <BulkEditDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        carIds={selectedIds}
        onDone={() => {
          setBulkOpen(false);
          setSelected({});
          load();
        }}
      />

      <ScheduleLagerDialog
        car={scheduleCar}
        onClose={() => setScheduleCar(null)}
        onSaved={() => { setScheduleCar(null); load(); }}
      />

      <StatusChangeDialog
        move={moveCar}
        onCancel={() => setMoveCar(null)}
        onConfirm={performMove}
      />

      <DeleteCarDialog
        cars={
          deleteCar
            ? Array.isArray(deleteCar)
              ? deleteCar
              : [deleteCar]
            : null
        }
        onClose={() => setDeleteCar(null)}
        onDeleted={() => { setDeleteCar(null); setSelected({}); load(); }}
      />

      <CarbuddiEarningsDialog
        key={carbuddiEarningCar?.id || "none"}
        car={carbuddiEarningCar}
        onClose={() => setCarbuddiEarningCar(null)}
        onSaved={() => { setCarbuddiEarningCar(null); load(); }}
      />
    </div>
  );
}

function StatusChangeDialog({ move, onCancel, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [updateCosts, setUpdateCosts] = useState(true);
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [defaults, setDefaults] = useState({ active: null, lager: null });

  useEffect(() => {
    if (!move) return;
    setLoading(true);
    setUpdateCosts(true);
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    api.get("/settings")
      .then((r) => setDefaults({
        active: r.data.default_fixed_costs,
        lager: r.data.default_fixed_costs_lager,
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [move]);

  if (!move) return null;
  const { car, newStatus } = move;
  const label = { active: "Aktiv (i drift)", lager: "Lager (ikke i drift)" }[newStatus];
  const newCosts = defaults[newStatus];
  const currentCosts = car.fixed_costs || {};

  const diff = FIXED_COST_FIELDS.map((f) => ({
    key: f.key,
    label: f.label,
    current: Number(currentCosts[f.key] || 0),
    next: Number(newCosts?.[f.key] || 0),
  }));

  const confirm = () => onConfirm({ car, newStatus, updateCosts, effectiveDate, newCosts });

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="rounded-none max-w-xl" data-testid="status-change-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl">
            Flyt {car.license_plate} til {label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-sm text-zinc-400">
            {newStatus === "lager"
              ? "Bilen sættes på lager og er ikke længere i aktiv drift."
              : "Bilen aktiveres og er i drift."}
          </p>

          <label className="flex items-start gap-3 cursor-pointer border border-zinc-700 p-3 hover:bg-[#262626]">
            <input
              type="checkbox"
              checked={updateCosts}
              onChange={(e) => setUpdateCosts(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[#C8A661] cursor-pointer"
              data-testid="update-costs-toggle"
            />
            <div className="flex-1">
              <div className="font-medium text-white">Opdater faste omkostninger til {label}-standard</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Erstat bilens nuværende omk. med standard-sættet for denne status.
              </div>
            </div>
          </label>

          {updateCosts && !loading && newCosts && (
            <div className="border border-zinc-800 bg-[#1A1A1A] p-4 space-y-3">
              <div>
                <Label className="overline">Gælder fra</Label>
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="rounded-none border-zinc-700 mt-2 w-48"
                  data-testid="effective-date-input"
                />
                <div className="text-xs text-zinc-500 mt-1">
                  Dato for registrering (bruges til fremtidig ROI-beregning pr. periode).
                </div>
              </div>
              <div>
                <Label className="overline">Omkostnings-ændringer</Label>
                <div className="mt-2 text-xs font-mono">
                  {diff.map((d) => {
                    const changed = d.current !== d.next;
                    return (
                      <div key={d.key} className={"flex justify-between py-0.5 " + (changed ? "text-[#C8A661]" : "text-zinc-500")}>
                        <span>{d.label}</span>
                        <span>{d.current.toLocaleString("da-DK")} → <strong>{d.next.toLocaleString("da-DK")} kr</strong></span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!updateCosts && (
            <div className="text-xs text-zinc-500 border border-zinc-800 p-3 bg-[#1A1A1A]">
              Omkostninger beholdes som de er. Du kan altid rette dem manuelt på bilen senere.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-none border-zinc-700"
            onClick={onCancel} data-testid="status-change-cancel">
            Annuller
          </Button>
          <Button type="button"
            className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574]"
            onClick={confirm} disabled={loading}
            data-testid="status-change-confirm">
            Flyt til {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CarDialog({ open, onOpenChange, car, onSaved, defaultStatus = "active" }) {
  const isEdit = !!car;
  const [form, setForm] = useState(emptyForm(defaultStatus));
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupInfo, setLookupInfo] = useState(null); // { cached, summary }

  const runLookup = async (force = false) => {
    const plate = (form.license_plate || "").trim().toUpperCase();
    if (!plate) {
      toast.error("Indtast en nummerplade først");
      return;
    }
    setLookupLoading(true);
    try {
      const { data } = await api.get(`/cars/lookup/${encodeURIComponent(plate)}`, {
        params: force ? { force: true } : {},
      });
      const s = data.summary || {};
      setForm((f) => ({
        ...f,
        make: s.make || f.make,
        model: s.model || f.model,
        year: s.year || f.year,
        // Hvis km felt er tomt og syn har kilometertal, brug det som forslag
        current_km:
          (f.current_km === "" || f.current_km == null) && s.mot_mileage
            ? s.mot_mileage
            : f.current_km,
        // Gem ekstra notater (variant, farve, vin) i notes-feltet hvis tomt
        notes: f.notes
          ? f.notes
          : [
              s.variant && `Variant: ${s.variant}`,
              s.fuel_type && `Brændstof: ${s.fuel_type}`,
              s.color && `Farve: ${s.color}`,
              s.first_registration && `1. reg.: ${s.first_registration}`,
              s.vin && `VIN: ${s.vin}`,
            ]
              .filter(Boolean)
              .join("\n"),
      }));
      setLookupInfo({ cached: !!data.cached, summary: s });
      toast.success(
        data.cached ? "Data hentet fra cache (ingen API-kvote brugt)" : "Data hentet fra MotorAPI"
      );
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => {
    if (car) {
      setForm({
        license_plate: car.license_plate || "",
        make: car.make || "",
        model: car.model || "",
        year: car.year || "",
        current_km: car.current_km ?? "",
        last_service_date: car.last_service_date || "",
        last_service_km: car.last_service_km ?? "",
        service_interval_km: car.service_interval_km ?? "",
        service_notes: car.service_notes || "",
        notes: car.notes || "",
        fixed_costs: { ...emptyForm().fixed_costs, ...(car.fixed_costs || {}) },
        extra_costs: Array.isArray(car.extra_costs) ? car.extra_costs.map((x) => ({ ...x })) : [],
        purchase_price: car.purchase_price ?? "",
        purchase_date: car.purchase_date || "",
        status: car.status || "active",
        platform: car.platform || "gomore",
        sale_price: car.sale_price ?? "",
        sale_date: car.sale_date || "",
      });
    } else {
      setForm(emptyForm(defaultStatus));
    }
    setLookupInfo(null);
  }, [car, open, defaultStatus]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = sanitize(form);
      if (isEdit) {
        await api.patch(`/cars/${car.id}`, body);
        toast.success("Bil opdateret");
      } else {
        await api.post("/cars", body);
        toast.success("Bil tilføjet");
      }
      onSaved();
    } catch (e2) {
      toast.error(formatErr(e2));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="car-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl">
            {isEdit ? "Rediger bil" : "Tilføj bil"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <datalist id="car-brands">
            {CAR_BRANDS.map((b) => <option key={b} value={b} />)}
          </datalist>
          <datalist id="car-models">
            {CAR_MODELS.map((m) => <option key={m} value={m} />)}
          </datalist>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Nummerplade" required>
              <div className="flex gap-2">
                <Input
                  value={form.license_plate}
                  onChange={(e) => setForm({ ...form, license_plate: e.target.value.toUpperCase() })}
                  className="rounded-none border-zinc-700 uppercase flex-1"
                  data-testid="car-license-input"
                  required
                />
                <Button
                  type="button"
                  onClick={() => runLookup(false)}
                  disabled={lookupLoading || !form.license_plate}
                  className="rounded-none bg-amber-500 hover:bg-amber-400 text-black font-semibold whitespace-nowrap px-3"
                  data-testid="car-lookup-btn"
                  title={isEdit ? "Slå op igen via MotorAPI (force)" : "Hent data fra MotorAPI"}
                >
                  {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Slå op"}
                </Button>
              </div>
              {lookupInfo && (
                <div
                  className="text-[11px] text-zinc-400 mt-1"
                  data-testid="car-lookup-info"
                >
                  {lookupInfo.cached ? "✓ Cache (0 API-kvote brugt)" : "✓ Hentet fra MotorAPI"}
                  {lookupInfo.summary?.mot_mileage
                    ? ` · Senest syn: ${lookupInfo.summary.mot_mileage.toLocaleString("da-DK")} km`
                    : ""}
                </div>
              )}
            </Field>
            <Field label="Årgang">
              <Input
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                className="rounded-none border-zinc-700"
                data-testid="car-year-input"
              />
            </Field>
            <Field label="Mærke">
              <Input
                list="car-brands"
                value={form.make}
                onChange={(e) => setForm({ ...form, make: e.target.value })}
                className="rounded-none border-zinc-700"
                data-testid="car-make-input"
                placeholder="Skriv fx 'sk' for Skoda"
              />
            </Field>
            <Field label="Model">
              <Input
                list="car-models"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="rounded-none border-zinc-700"
                data-testid="car-model-input"
                placeholder="Fx Picanto, Aygo, Polo"
              />
            </Field>
            <Field label="Nuværende km">
              <Input type="number" value={form.current_km}
                onChange={(e) => setForm({ ...form, current_km: e.target.value })}
                className="rounded-none border-zinc-700" data-testid="car-currentkm-input" />
            </Field>
            <Field label="Sidste service dato">
              <Input type="date" value={form.last_service_date}
                onChange={(e) => setForm({ ...form, last_service_date: e.target.value })}
                className="rounded-none border-zinc-700" data-testid="car-lastservice-date-input" />
            </Field>
            <Field label="Sidste service km">
              <Input type="number" value={form.last_service_km}
                onChange={(e) => setForm({ ...form, last_service_km: e.target.value })}
                className="rounded-none border-zinc-700" data-testid="car-lastservice-km-input" />
            </Field>
            <Field label="Service-interval (km)">
              <Input type="number" value={form.service_interval_km}
                onChange={(e) => setForm({ ...form, service_interval_km: e.target.value })}
                className="rounded-none border-zinc-700"
                placeholder="15000"
                data-testid="car-service-interval-input" />
            </Field>
            <Field label="Købspris (DKK)">
              <Input type="number" value={form.purchase_price}
                onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                className="rounded-none border-zinc-700" data-testid="car-purchase-price-input" />
            </Field>
            <Field label="Købsdato">
              <Input type="date" value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                className="rounded-none border-zinc-700" data-testid="car-purchase-date-input" />
            </Field>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <div className="cost-group-label">Faste omkostninger · uden moms-fradrag (ex. moms)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {FIXED_COST_FIELDS.filter((f) => f.group === "no_vat").map((f) => (
                <Field key={f.key} label={`${f.label} ${f.suffix}`}>
                  <Input
                    type="number"
                    value={form.fixed_costs[f.key]}
                    onChange={(e) =>
                      setForm({ ...form, fixed_costs: { ...form.fixed_costs, [f.key]: e.target.value } })
                    }
                    className="rounded-none border-zinc-700"
                    data-testid={`car-cost-${f.key}-input`}
                  />
                </Field>
              ))}
            </div>
            <div className="cost-group-label">Faste omkostninger · med moms-fradrag (ex. moms)</div>
            <div className="grid grid-cols-2 gap-4">
              {FIXED_COST_FIELDS.filter((f) => f.group === "vat").map((f) => (
                <Field key={f.key} label={`${f.label} ${f.suffix}`}>
                  <Input
                    type="number"
                    value={form.fixed_costs[f.key]}
                    onChange={(e) =>
                      setForm({ ...form, fixed_costs: { ...form.fixed_costs, [f.key]: e.target.value } })
                    }
                    className="rounded-none border-zinc-700"
                    data-testid={`car-cost-${f.key}-input`}
                  />
                </Field>
              ))}
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Ekstra omkostninger</div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm({
                    ...form,
                    extra_costs: [...form.extra_costs, { name: "", amount: 0, frequency: "once", vat_included: false }],
                  })
                }
                className="rounded-none border-zinc-700 h-8 text-xs bg-zinc-900 hover:bg-zinc-800"
                data-testid="add-extra-cost-button"
              >
                + Tilføj omkostning
              </Button>
            </div>
            {form.extra_costs.length === 0 && (
              <div className="text-xs text-zinc-500">Ingen ekstra omkostninger. Tilføj fx parkering, vask, dæk-skift…</div>
            )}
            <div className="space-y-2">
              {form.extra_costs.map((ec, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`extra-cost-row-${idx}`}>
                  <Input
                    placeholder="Navn (fx Parkering)"
                    value={ec.name}
                    onChange={(e) => {
                      const next = [...form.extra_costs];
                      next[idx] = { ...next[idx], name: e.target.value };
                      setForm({ ...form, extra_costs: next });
                    }}
                    className="rounded-none border-zinc-700 col-span-4"
                    data-testid={`extra-cost-name-${idx}`}
                  />
                  <Input
                    type="number"
                    placeholder="Beløb"
                    value={ec.amount}
                    onChange={(e) => {
                      const next = [...form.extra_costs];
                      next[idx] = { ...next[idx], amount: e.target.value };
                      setForm({ ...form, extra_costs: next });
                    }}
                    className="rounded-none border-zinc-700 col-span-2"
                    data-testid={`extra-cost-amount-${idx}`}
                  />
                  <select
                    value={ec.frequency}
                    onChange={(e) => {
                      const next = [...form.extra_costs];
                      next[idx] = { ...next[idx], frequency: e.target.value };
                      setForm({ ...form, extra_costs: next });
                    }}
                    className="border border-zinc-700 rounded-none px-2 py-2 text-sm col-span-3"
                    data-testid={`extra-cost-freq-${idx}`}
                  >
                    <option value="once">Engangs</option>
                    <option value="monthly">Pr. måned</option>
                    <option value="halfyearly">Pr. 6 mdr</option>
                    <option value="yearly">Pr. år</option>
                  </select>
                  <label className="col-span-2 flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!ec.vat_included}
                      onChange={(e) => {
                        const next = [...form.extra_costs];
                        next[idx] = { ...next[idx], vat_included: e.target.checked };
                        setForm({ ...form, extra_costs: next });
                      }}
                      className="w-3.5 h-3.5 cursor-pointer"
                      data-testid={`extra-cost-vat-${idx}`}
                    />
                    Inkl. moms
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const next = form.extra_costs.filter((_, i) => i !== idx);
                      setForm({ ...form, extra_costs: next });
                    }}
                    className="col-span-1 text-zinc-500 hover:text-red-400 text-sm"
                    data-testid={`extra-cost-remove-${idx}`}
                    title="Fjern"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="border border-zinc-700 rounded-none px-3 py-2 text-sm w-full"
                  data-testid="car-status-select"
                >
                  <option value="lager">Lager (købt, ikke i drift)</option>
                  <option value="active">Aktiv (i drift)</option>
                  {isEdit && <option value="sold">Solgt</option>}
                </select>
              </Field>
              {form.status === "active" && (
                <Field label="Platform">
                  <select
                    value={form.platform}
                    onChange={(e) => setForm({ ...form, platform: e.target.value })}
                    className="border border-zinc-700 rounded-none px-3 py-2 text-sm w-full"
                    data-testid="car-platform-select"
                  >
                    <option value="gomore">GoMore</option>
                    <option value="carbuddi">Carbuddi</option>
                  </select>
                </Field>
              )}
              {isEdit && form.status === "sold" && (
                <>
                  <Field label="Salgspris (DKK)">
                    <Input type="number" value={form.sale_price}
                      onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                      className="rounded-none border-zinc-700" data-testid="car-sale-price-input" />
                  </Field>
                  <Field label="Salgsdato">
                    <Input type="date" value={form.sale_date}
                      onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                      className="rounded-none border-zinc-700" data-testid="car-sale-date-input" />
                  </Field>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none border-zinc-700"
              onClick={() => onOpenChange(false)} data-testid="car-cancel-button">
              Annuller
            </Button>
            <Button type="submit" className="rounded-none bg-white text-black hover:bg-zinc-200"
              data-testid="car-save-button">
              {isEdit ? "Gem ændringer" : "Tilføj bil"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkEditDialog({ open, onOpenChange, carIds, onDone }) {
  const [costs, setCosts] = useState({ forsikring: "", vaegtafgift: "", leasing: "", noeglefri: "", vejhjaelp: "" });
  const [enabled, setEnabled] = useState({});

  useEffect(() => {
    if (open) {
      setCosts({ forsikring: "", vaegtafgift: "", leasing: "", noeglefri: "", vejhjaelp: "" });
      setEnabled({});
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const fc = {};
    FIXED_COST_FIELDS.forEach((f) => {
      if (enabled[f.key]) fc[f.key] = Number(costs[f.key] || 0);
    });
    if (Object.keys(fc).length === 0) {
      toast.error("Vælg mindst ét felt at opdatere");
      return;
    }
    // Need to merge with existing per-car cost; simpler: send as a partial fixed_costs object
    // But our backend replaces whole fixed_costs object. So we need to fetch each car and merge.
    try {
      // Fetch current cars
      const all = await api.get("/cars");
      const byId = Object.fromEntries(all.data.map((c) => [c.id, c]));
      // For consistency, do bulk by patching each car so we don't overwrite unchanged fields
      await Promise.all(
        carIds.map((id) => {
          const cur = byId[id];
          const merged = { ...(cur?.fixed_costs || {}), ...fc };
          return api.patch(`/cars/${id}`, { fixed_costs: merged });
        }),
      );
      toast.success(`Opdaterede ${carIds.length} biler`);
      onDone();
    } catch (e2) {
      toast.error(formatErr(e2));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl">
            Rediger {carIds.length} biler
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-zinc-500">
            Markér de felter der skal opdateres. Ikke-markerede felter forbliver uændrede pr. bil.
          </p>
          {FIXED_COST_FIELDS.map((f) => (
            <div key={f.key} className="flex items-center gap-3">
              <Checkbox
                checked={!!enabled[f.key]}
                onCheckedChange={(v) => setEnabled((s) => ({ ...s, [f.key]: !!v }))}
                data-testid={`bulk-enable-${f.key}`}
              />
              <Label className="w-32">{f.label}</Label>
              <Input
                type="number"
                value={costs[f.key]}
                disabled={!enabled[f.key]}
                onChange={(e) => setCosts({ ...costs, [f.key]: e.target.value })}
                className="rounded-none border-zinc-700 flex-1"
                data-testid={`bulk-value-${f.key}`}
              />
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none border-zinc-700"
              onClick={() => onOpenChange(false)}>Annuller</Button>
            <Button type="submit" className="rounded-none bg-white text-black hover:bg-zinc-200"
              data-testid="bulk-save-button">
              Anvend på {carIds.length}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="overline">{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function emptyForm(defaultStatus = "active") {
  return {
    license_plate: "",
    make: "",
    model: "",
    year: "",
    current_km: "",
    last_service_date: "",
    last_service_km: "",
    service_interval_km: "",
    service_notes: "",
    notes: "",
    fixed_costs: { forsikring: 0, vejhjaelp: 0, vaegtafgift: 0, laan: 0, noeglefri: 0, leasing: 0 },
    extra_costs: [],
    purchase_price: "",
    purchase_date: "",
    status: defaultStatus,
    platform: "gomore",
    sale_price: "",
    sale_date: "",
  };
}

function sanitize(form) {
  const o = { ...form };
  ["year", "current_km", "last_service_km", "service_interval_km"].forEach((k) => {
    o[k] = o[k] === "" ? null : Number(o[k]);
  });
  ["sale_price", "purchase_price"].forEach((k) => {
    o[k] = o[k] === "" ? null : Number(o[k]);
  });
  ["last_service_date", "sale_date", "purchase_date"].forEach((k) => {
    if (o[k] === "") o[k] = null;
  });
  o.fixed_costs = Object.fromEntries(
    Object.entries(o.fixed_costs).map(([k, v]) => [k, Number(v || 0)]),
  );
  o.extra_costs = (o.extra_costs || [])
    .filter((ec) => (ec.name || "").trim() !== "")
    .map((ec) => ({
      name: ec.name.trim(),
      amount: Number(ec.amount || 0),
      frequency: ec.frequency || "monthly",
      vat_included: !!ec.vat_included,
    }));
  return o;
}

function ScheduleLagerDialog({ car, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [costs, setCosts] = useState({
    insurance: 0, weight_tax: 0, loan: 0, key_subscription: 0, leasing: 0,
    insurance_non_vat: 0, weight_tax_non_vat: 0, loan_non_vat: 0,
  });
  const [saving, setSaving] = useState(false);
  const [presetLoaded, setPresetLoaded] = useState(false);

  useEffect(() => {
    if (car) {
      setDate(car.scheduled_lager_date || "");
      // Hent default lager-omk hvis ikke tidligere planlagt
      if (car.scheduled_lager_costs) {
        setCosts({ ...costs, ...car.scheduled_lager_costs });
      } else if (!presetLoaded) {
        api.get("/settings").then((r) => {
          if (r.data?.default_fixed_costs_lager) {
            setCosts({ ...costs, ...r.data.default_fixed_costs_lager });
          }
          setPresetLoaded(true);
        }).catch(() => setPresetLoaded(true));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.id]);

  if (!car) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/cars/${car.id}/schedule-lager`, {
        scheduled_lager_date: date || null,
        scheduled_lager_costs: date ? costs : null,
      });
      toast.success(date ? "Plan gemt" : "Plan annulleret");
      onSaved();
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Annuller planlagt udfasning?")) return;
    setSaving(true);
    try {
      await api.post(`/cars/${car.id}/schedule-lager`, { scheduled_lager_date: null });
      toast.success("Plan annulleret");
      onSaved();
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setSaving(false); }
  };

  const COST_FIELDS = [
    { key: "insurance", label: "Forsikring", vat: true },
    { key: "weight_tax", label: "Vægtafgift", vat: true },
    { key: "loan", label: "Lån", vat: true },
    { key: "key_subscription", label: "Nøgle abn.", vat: true },
    { key: "leasing", label: "Leasing", vat: true },
    { key: "insurance_non_vat", label: "Forsikring (uden moms)", vat: false },
    { key: "weight_tax_non_vat", label: "Vægtafgift (uden moms)", vat: false },
    { key: "loan_non_vat", label: "Lån (uden moms)", vat: false },
  ];

  return (
    <Dialog open={!!car} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="schedule-lager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl">
            Plan udfasning · {car.license_plate}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">Dato hvor bilen rykkes til lager</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-none border-zinc-700 mt-2"
              data-testid="schedule-date-input"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Faste omk. som aktiv stopper på denne dato. Bilen flyttes automatisk når datoen rammes.
            </p>
          </div>
          <div>
            <Label className="overline">Faste omk. på lager (månedlig, ex moms)</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {COST_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-[10px] text-zinc-500 uppercase">{f.label}</label>
                  <Input
                    type="number"
                    value={costs[f.key] || 0}
                    onChange={(e) => setCosts({ ...costs, [f.key]: Number(e.target.value) || 0 })}
                    className="rounded-none border-zinc-700 h-8 text-sm"
                    data-testid={`schedule-cost-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {car.scheduled_lager_date && (
              <Button type="button" variant="outline" onClick={cancel} disabled={saving} className="rounded-none border-red-800 text-red-400">
                Annuller plan
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700">
              Luk
            </Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574]" data-testid="schedule-save-button">
              {saving ? "Gemmer…" : "Gem plan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function CarbuddiEarningsDialog({ car, onClose, onSaved }) {
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!car) return;
    setNote("");
    setExisting(null);
    setAmount("");
    api.get("/carbuddi/earnings", { params: { car_id: car.id } })
      .then((r) => {
        const found = (r.data || []).find((x) => x.year_month === ym);
        if (found) {
          setExisting(found);
          setAmount(String(found.amount ?? ""));
          setNote(found.note || "");
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car, ym]);

  if (!car) return null;

  const amountNum = parseFloat(amount) || 0;
  const exVat = amountNum / 1.25;

  const submit = async (e) => {
    e.preventDefault();
    if (amountNum <= 0) {
      toast.error("Indtast et beløb større end 0");
      return;
    }
    setBusy(true);
    try {
      await api.put("/carbuddi/earnings", {
        car_id: car.id,
        year_month: ym,
        amount: amountNum,
        vat_included: true,
        note,
      });
      toast.success("Indtjening gemt");
      if (onSaved) onSaved(); else onClose();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!car} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#1A1A1A] border-zinc-700 max-w-md rounded-none" data-testid="carbuddi-earnings-dialog">
        <DialogHeader>
          <div className="overline text-[#C8A661] mb-1">Carbuddi indtjening</div>
          <DialogTitle className="headline text-2xl">{car.license_plate} <span className="text-zinc-400 font-normal text-base">{car.make} {car.model}</span></DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">Måned</Label>
            <Input
              type="month"
              value={ym}
              onChange={(e) => setYm(e.target.value)}
              className="rounded-none border-zinc-700 mt-2"
              data-testid="carbuddi-month"
            />
            {existing && (
              <p className="text-[11px] text-amber-400 mt-1.5" data-testid="carbuddi-existing-note">
                Eksisterende værdi indlæst — gem for at opdatere.
              </p>
            )}
          </div>

          <div>
            <Label className="overline">Beløb (DKK · inkl. moms)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Fx 6 250"
              className="rounded-none border-zinc-700 mt-2 font-mono text-lg"
              autoFocus
              data-testid="carbuddi-amount"
            />
            {amountNum > 0 && (
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Ekskl. moms: <strong className="text-zinc-300">{exVat.toLocaleString("da-DK", { maximumFractionDigits: 2 })} kr.</strong> (gemt i regnskab)
              </p>
            )}
          </div>

          <div>
            <Label className="overline">Note (valgfri)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Fx ekstra kørselsbonus"
              className="rounded-none border-zinc-700 mt-2"
              data-testid="carbuddi-note"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700">Annullér</Button>
            <Button
              type="submit"
              disabled={busy || amountNum <= 0}
              className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574] disabled:opacity-40"
              data-testid="carbuddi-save"
            >
              {busy ? "Gemmer…" : "Gem indtjening"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
